/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Comps.tsx
 * Transaction and rent comp engine for the Terminal.
 * Shows comparable sales and active rent listings for any MSA.
 * Rendered as a tab inside the Terminal view.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = 'https://coast-terminal-production.up.railway.app';

interface Comp {
  id: number;
  comp_type: 'transaction' | 'rent' | 'listing';
  property_name: string | null;
  address: string | null;
  city: string;
  state: string;
  units: number | null;
  year_built: number | null;
  price: number | null;
  price_per_unit: number | null;
  cap_rate: number | null;
  monthly_rent: number | null;
  sqft: number | null;
  asset_class: string | null;
  sale_date: string | null;
  source: string;
  source_url: string | null;
  fetched_at: string;
}

interface CompSummary {
  transaction_count: number;
  rent_count: number;
  avg_price_per_unit: number | null;
  avg_cap_rate: number | null;
  avg_monthly_rent: number | null;
}

interface CompsProps {
  token: string;
  msa: string;
  subjectUnits?: number;
  subjectPrice?: number;
  subjectYearBuilt?: number;
}

const Comps: React.FC<CompsProps> = ({ token, msa, subjectUnits, subjectPrice, subjectYearBuilt }) => {
  const gold = '#b8956a';
  const goldDim = 'rgba(201,169,110,0.08)';
  const goldBorder = 'rgba(201,169,110,0.25)';
  const green = '#1A7A4A';
  const red = '#CC2200';
  const bg = '#ffffff';
  const surface = '#fafaf8';
  const surfaceRaised = '#f2ede6';
  const surfaceHigh = '#e6ddd4';
  const border = '#e6ddd4';
  const borderLight = '#e6ddd4';
  const textPrimary = '#0d3d5c';
  const textSecondary = '#12526e';
  const textMuted = '#2d7fa0';
  const serifFont = "'Fraunces', Georgia, serif";

  const headers = { Authorization: `Bearer ${token}` };

  const [comps, setComps] = useState<Comp[]>([]);
  const [summary, setSummary] = useState<CompSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'transaction' | 'rent'>('all');
  const [total, setTotal] = useState(0);

  const fetchComps = useCallback(async () => {
    setLoading(true);
    try {
      const encoded = encodeURIComponent(msa);
      const params: Record<string, any> = {};
      if (filter !== 'all') params.comp_type = filter;
      const res = await axios.get(`${API}/terminal/comps/${encoded}`, { headers, params });
      setComps(res.data.comps || []);
      setSummary(res.data.summary || null);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Comps fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [msa, filter]); // eslint-disable-line

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const encoded = encodeURIComponent(msa);
      await axios.post(`${API}/terminal/comps/refresh/${encoded}`, {}, { headers });
      await fetchComps();
    } catch {}
    finally { setRefreshing(false); }
  };

  const runAiAnalysis = async () => {
    setAiLoading(true);
    setAiAnalysis('');
    try {
      const res = await axios.post(`${API}/terminal/comps/analyze`, {
        msa,
        subject_units: subjectUnits,
        subject_price: subjectPrice,
        subject_year_built: subjectYearBuilt,
      }, { headers });
      setAiAnalysis(res.data.analysis);
    } catch {
      setAiAnalysis('Analysis failed. Ensure comps are loaded first.');
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    fetchComps();
  }, [fetchComps]);

  const fmt = {
    price: (v: number | null) => v ? `$${(v / 1e6).toFixed(1)}M` : '—',
    ppu: (v: number | null) => v ? `$${Math.round(v).toLocaleString()}/unit` : '—',
    rent: (v: number | null) => v ? `$${Math.round(v).toLocaleString()}/mo` : '—',
    cap: (v: number | null) => v ? `${v.toFixed(2)}%` : '—',
    units: (v: number | null) => v ? `${v}u` : '—',
  };

  const sourceColor = (source: string) => {
    if (source.includes('Crexi')) return '#1A7A4A';
    if (source.includes('Apartments')) return '#1A5A8A';
    if (source.includes('News')) return gold;
    return textMuted;
  };

  const typeColor = (type: string) => {
    if (type === 'transaction') return gold;
    if (type === 'rent') return green;
    return textSecondary;
  };

  const filteredComps = filter === 'all' ? comps : comps.filter(c => c.comp_type === filter);

  const renderAnalysis = (text: string) => text.split('\n').map((line, i) => {
    const t = line.trim();
    if (!t) return <div key={i} style={{ height: '8px' }} />;
    if (t.startsWith('## ') || t.startsWith('# '))
      return <div key={i} style={{ fontSize: '10px', fontWeight: 800, color: gold, letterSpacing: '2px', textTransform: 'uppercase', marginTop: '20px', marginBottom: '8px', paddingBottom: '6px', borderBottom: `1px solid ${border}` }}>{t.replace(/^#+\s/, '')}</div>;
    if (t.startsWith('**') && t.endsWith('**'))
      return <div key={i} style={{ fontSize: '13px', fontWeight: 700, color: textPrimary, marginTop: '12px', marginBottom: '4px', fontFamily: serifFont }}>{t.replace(/\*\*/g, '')}</div>;
    if (t.startsWith('- '))
      return (
        <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '4px' }}>
          <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: gold, marginTop: '8px', flexShrink: 0 }} />
          <div style={{ fontSize: '13px', color: textSecondary, lineHeight: 1.7, fontFamily: serifFont }} dangerouslySetInnerHTML={{ __html: t.replace(/^- /, '').replace(/\*\*([^*]+)\*\*/g, `<strong style="color:${textPrimary}">$1</strong>`) }} />
        </div>
      );
    return <div key={i} style={{ fontSize: '13px', color: textSecondary, lineHeight: 1.8, fontFamily: serifFont }} dangerouslySetInnerHTML={{ __html: t.replace(/\*\*([^*]+)\*\*/g, `<strong style="color:${textPrimary}">$1</strong>`) }} />;
  });

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${border}`, backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: '9px', color: gold, letterSpacing: '3px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>Comp Engine</div>
          <div style={{ fontSize: '18px', fontWeight: 400, color: textPrimary, fontFamily: serifFont }}>{msa}</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={handleRefresh} disabled={refreshing}
            style={{ backgroundColor: 'transparent', border: `1px solid ${borderLight}`, color: refreshing ? textMuted : textSecondary, padding: '6px 14px', fontSize: '10px', fontWeight: 700, cursor: refreshing ? 'default' : 'pointer', fontFamily: 'inherit', letterSpacing: '1px', textTransform: 'uppercase' }}>
            {refreshing ? 'Fetching...' : 'Refresh Comps'}
          </button>
          <button onClick={runAiAnalysis} disabled={aiLoading || comps.length === 0}
            style={{ backgroundColor: comps.length > 0 && !aiLoading ? gold : surfaceHigh, color: comps.length > 0 && !aiLoading ? bg : textMuted, border: 'none', padding: '6px 16px', fontSize: '10px', fontWeight: 800, cursor: comps.length > 0 && !aiLoading ? 'pointer' : 'default', fontFamily: 'inherit', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
            {aiLoading ? 'Analyzing...' : 'AI Analysis'}
          </button>
        </div>
      </div>

      {/* Summary strip */}
      {summary && (summary.avg_price_per_unit || summary.avg_cap_rate || summary.avg_monthly_rent) && (
        <div style={{ display: 'flex', gap: '1px', backgroundColor: border, flexShrink: 0 }}>
          {[
            { label: 'Avg $/Unit', value: summary.avg_price_per_unit ? `$${Math.round(summary.avg_price_per_unit).toLocaleString()}` : '—' },
            { label: 'Avg Cap Rate', value: summary.avg_cap_rate ? `${summary.avg_cap_rate.toFixed(2)}%` : '—' },
            { label: 'Avg Market Rent', value: summary.avg_monthly_rent ? `$${Math.round(summary.avg_monthly_rent).toLocaleString()}/mo` : '—' },
            { label: 'Transaction Comps', value: `${summary.transaction_count}` },
            { label: 'Rent Comps', value: `${summary.rent_count}` },
          ].map(({ label, value }) => (
            <div key={label} style={{ flex: 1, padding: '10px 14px', backgroundColor: '#fafaf8', textAlign: 'center' }}>
              <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '4px' }}>{label}</div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: gold, fontFamily: serifFont }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${border}`, flexShrink: 0 }}>
        {(['all', 'transaction', 'rent'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{ padding: '8px 18px', border: 'none', borderBottom: `2px solid ${filter === f ? gold : 'transparent'}`, cursor: 'pointer', fontSize: '10px', fontWeight: filter === f ? 700 : 500, letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'inherit', backgroundColor: 'transparent', color: filter === f ? gold : textMuted, transition: 'all 0.15s' }}>
            {f === 'all' ? `All (${total})` : f === 'transaction' ? `Sales (${summary?.transaction_count || 0})` : `Rents (${summary?.rent_count || 0})`}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* AI Analysis */}
        {aiAnalysis && (
          <div style={{ margin: '16px 20px', backgroundColor: '#ffffff', border: `1px solid ${border}`, borderLeft: `3px solid ${gold}`, padding: '18px 20px' }}>
            <div style={{ fontSize: '9px', color: gold, letterSpacing: '2px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '14px' }}>AI Comp Analysis</div>
            {renderAnalysis(aiAnalysis)}
          </div>
        )}

        {loading ? (
          <div style={{ padding: '20px' }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{ height: '52px', backgroundColor: '#ffffff', border: `1px solid ${border}`, marginBottom: '1px', display: 'flex', alignItems: 'center', padding: '0 16px', gap: '16px' }}>
                {[80, 120, 60, 60, 60].map((w, j) => (
                  <div key={j} style={{ width: `${w}px`, height: '10px', backgroundColor: '#fafaf8', borderRadius: '1px', animation: 'pulse 1.5s ease-in-out infinite' }} />
                ))}
              </div>
            ))}
          </div>
        ) : filteredComps.length === 0 ? (
          <div style={{ padding: '48px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: textMuted, fontFamily: serifFont, marginBottom: '12px' }}>No comps loaded yet.</div>
            <div style={{ fontSize: '11px', color: textMuted, marginBottom: '20px' }}>Click Refresh Comps to pull transaction and rent data for {msa.split(',')[0]}.</div>
            <button onClick={handleRefresh} disabled={refreshing}
              style={{ backgroundColor: gold, color: bg, border: 'none', padding: '10px 24px', fontSize: '11px', fontWeight: 800, letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit' }}>
              {refreshing ? 'Fetching...' : 'Fetch Comps Now'}
            </button>
          </div>
        ) : (
          <div style={{ backgroundColor: '#ffffff', border: `1px solid ${border}`, margin: '0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: '#fafaf8' }}>
                  {['Type', 'Property', 'Units', 'Yr Built', '$/Unit', 'Cap Rate', 'Rent/Mo', 'Source'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '9px 12px', fontSize: '8px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, borderBottom: `1px solid ${border}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredComps.map((comp, i) => {
                  const rowBg = i % 2 === 0 ? '#fafaf8' : '#ffffff';
                  return (
                  <tr key={comp.id}
                    style={{ backgroundColor: rowBg, borderBottom: `1px solid ${border}`, transition: 'background 0.1s', cursor: comp.source_url ? 'pointer' : 'default' }}
                    onClick={() => comp.source_url && window.open(comp.source_url, '_blank')}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = surfaceRaised)}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = rowBg)}>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ fontSize: '8px', fontWeight: 800, color: typeColor(comp.comp_type), letterSpacing: '1px', textTransform: 'uppercase' }}>
                        {comp.comp_type === 'transaction' ? 'SALE' : comp.comp_type === 'rent' ? 'RENT' : 'LIST'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', maxWidth: '200px' }}>
                      <div style={{ fontSize: '12px', color: textPrimary, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {comp.property_name || '—'}
                      </div>
                      {comp.address && (
                        <div style={{ fontSize: '10px', color: textMuted, marginTop: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {comp.address}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', color: textSecondary, whiteSpace: 'nowrap' }}>{fmt.units(comp.units)}</td>
                    <td style={{ padding: '10px 12px', color: textSecondary, whiteSpace: 'nowrap' }}>{comp.year_built || '—'}</td>
                    <td style={{ padding: '10px 12px', color: comp.price_per_unit ? gold : textMuted, fontWeight: comp.price_per_unit ? 700 : 400, whiteSpace: 'nowrap' }}>
                      {fmt.ppu(comp.price_per_unit)}
                    </td>
                    <td style={{ padding: '10px 12px', color: comp.cap_rate ? textPrimary : textMuted, whiteSpace: 'nowrap' }}>{fmt.cap(comp.cap_rate)}</td>
                    <td style={{ padding: '10px 12px', color: comp.monthly_rent ? green : textMuted, whiteSpace: 'nowrap' }}>{fmt.rent(comp.monthly_rent)}</td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: '9px', color: sourceColor(comp.source), fontWeight: 700 }}>{comp.source}</span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div>
  );
};

export default Comps;